<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Acampantes <span class="title-count">{{ filtered.length }}</span></h1>
      <button v-if="canManageAcampantes" class="btn-primary header-new" @click="openNew">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
        <span>Novo</span>
      </button>
    </div>

    <div class="toolbar">
      <div class="search-box">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="search-icon"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0c.41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z"/></svg>
        <input class="input search-input" type="text" v-model="searchQuery" placeholder="Buscar por nome ou telefone..." />
        <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''" aria-label="Limpar busca">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
        </button>
      </div>
      <button class="btn-filters" :class="{ 'has-active': activeFiltersCount > 0 }" @click="drawerOpen = true">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
        <span>Filtros</span>
        <span v-if="activeFiltersCount > 0" class="filters-badge">{{ activeFiltersCount }}</span>
      </button>
    </div>

    <div v-if="activeChips.length" class="active-chips">
      <span class="active-chips-label">Filtros ativos:</span>
      <button
        v-for="chip in activeChips"
        :key="chip.key + ':' + chip.value"
        class="active-chip"
        @click="setFilter(chip.key, 'ALL')"
        :title="`Remover filtro: ${chip.label}`"
      >
        <span>{{ chip.label }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
      </button>
      <button class="active-clear" @click="clearAllFilters">Limpar tudo</button>
    </div>

    <SkeletonLoader v-if="store.loading && store.acampantes.length === 0" variant="table-row" :count="6" />

    <div class="acampante-list" v-else-if="filtered.length > 0">
      <button
        type="button"
        class="acampante-item card"
        v-for="item in filtered"
        :key="item.id"
        :aria-label="`Ver detalhes de ${item.name}`"
        @click="openDetails(item)"
      >
        <div class="item-main">
          <div class="item-name-row">
            <span class="item-name">{{ item.name }}</span>
            <EquipeChip :equipe="item.equipe" />
            <TransporteBadge v-if="item.check && item.transporte" :value="item.transporte" size="small" />
            <span
              v-if="statusFinanceiro(item) === 'quitado'"
              class="fin-chip fin-chip-quitado"
              title="Pagamento quitado"
            >Quitado</span>
            <span
              v-else-if="statusFinanceiro(item) === 'negociado'"
              class="fin-chip fin-chip-negociado"
              :title="item.observacaoNegociacao || 'Acordo de pagamento'"
            >🤝 Negociado</span>
            <span
              v-else-if="statusFinanceiro(item) === 'devendo' && canSeeFinanceiro"
              class="fin-chip fin-chip-devendo"
              :title="`Falta ${formatBRL(item.falta)}`"
            >Falta {{ formatBRL(item.falta) }}</span>
            <span
              v-else-if="statusFinanceiro(item) === 'devendo'"
              class="fin-chip fin-chip-devendo"
              title="Encaminhar para o financeiro"
            >Devendo</span>
            <span
              v-if="item.fichaPreenchidaEm"
              class="ficha-badge ficha-badge-ok"
              title="Ficha de inscrição completa"
            ><i class="fa fa-clipboard-check"></i> Ficha</span>
            <span
              v-else
              class="ficha-badge ficha-badge-falta"
              title="Ficha de inscrição não preenchida"
            ><i class="fa fa-clipboard-list"></i> Falta ficha</span>
            <span
              v-if="isDiaria(item)"
              class="diaria-badge"
              :title="`Diária: ${diasLongChips(item.diasAcamp).join(', ')}`"
            >📅 {{ diasShortChips(item.diasAcamp) }}</span>
          </div>
          <div class="item-meta">
            <span v-if="item.contato" class="item-contato">{{ item.contato }}</span>
            <span class="item-status" :class="item.check ? 'status-confirmed' : 'status-pending'">
              <span v-if="item.check">✓ Confirmado</span>
              <span v-else>Aguardando</span>
            </span>
          </div>
        </div>

        <div class="item-actions" @click.stop>
          <a
            v-if="canManageAcampantes && tribusLinkFor(item)"
            class="icon-btn icon-tribus"
            :href="tribusLinkFor(item)"
            target="_blank"
            rel="noopener"
            :aria-label="`Enviar promo TRIBUS pra ${item.name}`"
            :title="`Enviar promo TRIBUS pra ${item.name}`"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488a11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592c5.448 0 9.886-4.434 9.889-9.885c.002-5.462-4.415-9.89-9.881-9.892c-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.667 5.39l-.999 3.648zM12.05 5.97c-.34-.011-.66-.011-.971.062c-.32.075-.71.273-1.072.638c-.371.366-1.402 1.37-1.402 3.345s1.435 3.881 1.635 4.146c.2.264 2.79 4.267 6.844 5.978c.961.413 1.722.66 2.317.842c.985.314 1.881.27 2.591.165c.79-.117 2.448-1.002 2.79-1.97c.343-.97.343-1.797.24-1.973c-.103-.175-.376-.279-.785-.487c-.41-.207-2.42-1.197-2.795-1.334c-.376-.137-.65-.205-.923.207c-.275.412-1.06 1.334-1.299 1.609c-.24.275-.479.309-.888.103c-.41-.207-1.732-.638-3.297-2.035c-1.219-1.085-2.04-2.422-2.279-2.832c-.24-.412-.024-.635.184-.84c.184-.184.41-.479.617-.718c.207-.24.276-.412.412-.687c.137-.275.07-.514-.034-.722c-.103-.207-.918-2.214-1.293-3.026c-.349-.755-.694-.638-.923-.638z"/></svg>
          </a>
          <button class="icon-btn" @click="openQr(item)" aria-label="Ver QR Code" title="Ver QR Code">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M3 11h8V3H3v8zm2-6h4v4H5V5zm-2 16h8v-8H3v8zm2-6h4v4H5v-4zm8-12v8h8V3h-8zm6 6h-4V5h4v4zm0 4h-2v2h2v-2zM11 13H9v4h2v-2h2v-2h-2v0zm6 0h-2v2h2v-2zm-2 4h-2v2h2v-2zm2-2h2v-2h-2v2zm0 6h2v-4h-2v4z"/></svg>
          </button>
          <template v-if="canManageAcampantes">
            <button class="icon-btn" @click="openEdit(item)" aria-label="Editar" title="Editar">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83l3.75 3.75l1.83-1.83z"/></svg>
            </button>
            <button class="icon-btn icon-danger" @click="confirmDelete(item)" aria-label="Remover" title="Remover">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </template>
        </div>
      </button>
    </div>

    <EmptyState
      v-else
      :title="hasFilters ? 'Nenhum acampante encontrado' : 'Nenhum acampante cadastrado'"
      :description="hasFilters ? 'Tente outro filtro ou termo de busca.' : 'Clique em \'Novo\' para cadastrar o primeiro.'"
    />

    <AcampanteForm
      :visible="formVisible"
      :participant="formTarget"
      :submitting="submitting"
      @save="handleSave"
      @cancel="closeForm"
    />

    <AcampanteDetailsModal
      :visible="!!detailsTarget"
      :participant="detailsTarget"
      @close="detailsTarget = null"
      @edit="editFromDetails"
      @qr="qrFromDetails"
      @add-pagamento="openPagamento"
      @delete-pagamento="confirmDeletePagamento"
      @open-ficha="openFichaAdmin"
    />

    <!-- Modal admin da ficha (z-index acima do details) -->
    <div v-if="fichaTarget" class="modal-overlay ficha-overlay" @click.self="fichaTarget = null">
      <div class="modal-card ficha-modal">
        <header class="ficha-modal-header">
          <h2 class="modal-title">Ficha de inscrição — {{ fichaTarget.name }}</h2>
          <button class="close-x" @click="fichaTarget = null" aria-label="Fechar">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
          </button>
        </header>
        <InscricaoForm
          mode="admin"
          :acampante="fichaTarget"
          :initial-data="fichaTarget"
          @submit-success="handleFichaSaved"
          @cancel="fichaTarget = null"
          @reopen="handleFichaReopened"
        />
      </div>
    </div>

    <PagamentoModal
      :visible="!!pagamentoTarget"
      :participant="pagamentoTarget"
      :submitting="savingPagamento"
      @save="handleSavePagamento"
      @cancel="pagamentoTarget = null"
    />

    <div class="modal-overlay" v-if="deletePagamentoTarget" @click.self="deletePagamentoTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Remover pagamento?</h2>
        <p class="confirm-text">
          Remover pagamento de <strong>{{ formatBRL(deletePagamentoTarget.pag.valor) }}</strong>
          de <strong>{{ deletePagamentoTarget.participant.name }}</strong>?
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" @click="deletePagamentoTarget = null">Cancelar</button>
          <button class="btn-danger" :disabled="deletingPagamento" @click="handleDeletePagamento">
            <span v-if="!deletingPagamento">Remover</span>
            <span v-else>Removendo...</span>
          </button>
        </div>
      </div>
    </div>

    <div class="modal-overlay" v-if="qrTarget" @click.self="qrTarget = null">
      <div class="modal-card qr-modal">
        <h2 class="modal-title">QR Code</h2>
        <p class="qr-name">{{ qrTarget.name }}</p>
        <EquipeChip :equipe="qrTarget.equipe" />
        <div class="qr-display">
          <QrCodeDisplay :value="qrTarget.id" :size="280" />
        </div>
        <p class="qr-hint">Aponte o scanner para este código para fazer o check-in.</p>
        <div class="modal-actions">
          <button class="btn-secondary" @click="qrTarget = null">Fechar</button>
        </div>
      </div>
    </div>

    <div class="modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="modal-card">
        <h2 class="modal-title">Remover acampante?</h2>
        <p class="confirm-text">
          Tem certeza que deseja remover <strong>{{ deleteTarget.name }}</strong>? Esta ação não pode ser desfeita.
        </p>
        <div class="modal-actions">
          <button class="btn-secondary" @click="deleteTarget = null">Cancelar</button>
          <button class="btn-danger" :disabled="deleting" @click="executeDelete">
            <span v-if="!deleting">Remover</span>
            <span v-else>Removendo...</span>
          </button>
        </div>
      </div>
    </div>

    <FiltersDrawer
      :visible="drawerOpen"
      :filters="drawerFilters"
      :sections="drawerSections"
      @close="drawerOpen = false"
      @update:filters="onDrawerUpdate"
    />
  </div>
</template>

<script>
import { useAcampantesStore } from '../stores/acampantes'
import { useAuthStore } from '../stores/auth'
import { EQUIPES } from '../lib/equipes'
import AcampanteForm from '../components/AcampanteForm.vue'
import AcampanteDetailsModal from '../components/AcampanteDetailsModal.vue'
import InscricaoForm from '../components/InscricaoForm.vue'
import PagamentoModal from '../components/PagamentoModal.vue'
import EquipeChip from '../components/EquipeChip.vue'
import TransporteBadge from '../components/TransporteBadge.vue'
import QrCodeDisplay from '../components/QrCodeDisplay.vue'
import SkeletonLoader from '../components/ui/SkeletonLoader.vue'
import EmptyState from '../components/ui/EmptyState.vue'
import FiltersDrawer from '../components/ui/FiltersDrawer.vue'
import { formatBRL, statusFinanceiro } from '../lib/financeiro'
import { isDiaria, diasShortChips, diasLongChips } from '../lib/dias'
import { buildTribusWaLink } from '../lib/campanhas'
import { ACOMODACOES } from '../lib/acomodacoes'

function normalize(s) {
  return (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export default {
  name: 'AcampantesPage',
  components: { AcampanteForm, AcampanteDetailsModal, InscricaoForm, PagamentoModal, EquipeChip, TransporteBadge, QrCodeDisplay, SkeletonLoader, EmptyState, FiltersDrawer },
  data() {
    return {
      equipes: EQUIPES,
      drawerOpen: false,
      formVisible: false,
      formTarget: null,
      submitting: false,
      qrTarget: null,
      deleteTarget: null,
      deleting: false,
      detailsTarget: null,
      fichaTarget: null,
      pagamentoTarget: null,
      savingPagamento: false,
      deletePagamentoTarget: null,
      deletingPagamento: false
    }
  },
  computed: {
    store() { return useAcampantesStore() },
    authStore() { return useAuthStore() },
    canSeeFinanceiro() { return this.authStore.canSeeFinanceiro },
    canManageAcampantes() { return this.authStore.canManageAcampantes },
    searchQuery: {
      get() { return this.$route.query.q || '' },
      set(v) { this.setFilter('q', v) }
    },
    equipeFilter: {
      get() { return this.$route.query.equipe || 'ALL' },
      set(v) { this.setFilter('equipe', v) }
    },
    finFilter: {
      get() { return this.$route.query.fin || 'ALL' },
      set(v) { this.setFilter('fin', v) }
    },
    fichaFilter: {
      get() { return this.$route.query.ficha || 'ALL' },
      set(v) { this.setFilter('ficha', v) }
    },
    tipoFilter: {
      get() { return this.$route.query.tipo || 'ALL' },
      set(v) { this.setFilter('tipo', v) }
    },
    acomodacaoFilter: {
      get() { return this.$route.query.acomodacao || 'ALL' },
      set(v) { this.setFilter('acomodacao', v) }
    },
    transporteFilter: {
      get() { return this.$route.query.transporte || 'ALL' },
      set(v) { this.setFilter('transporte', v) }
    },
    hasFilters() {
      return (this.searchQuery || '').trim() !== ''
        || this.equipeFilter !== 'ALL'
        || this.finFilter !== 'ALL'
        || this.fichaFilter !== 'ALL'
        || this.tipoFilter !== 'ALL'
        || this.acomodacaoFilter !== 'ALL'
        || this.transporteFilter !== 'ALL'
    },
    activeFiltersCount() {
      return [
        this.equipeFilter, this.finFilter, this.fichaFilter,
        this.tipoFilter, this.acomodacaoFilter, this.transporteFilter
      ].filter(v => v !== 'ALL').length
    },
    drawerFilters() {
      return {
        equipe: this.equipeFilter,
        fin: this.finFilter,
        ficha: this.fichaFilter,
        tipo: this.tipoFilter,
        acomodacao: this.acomodacaoFilter,
        transporte: this.transporteFilter
      }
    },
    drawerSections() {
      const c = this.counts
      const equipeOptions = [{ value: 'ALL', label: 'Todas' }]
      for (const e of this.equipes) {
        equipeOptions.push({ value: e.value, label: e.label, count: this.store.byEquipe(e.value).length })
      }
      if (this.semEquipeCount > 0) {
        equipeOptions.push({ value: 'NONE', label: 'Sem equipe', count: this.semEquipeCount })
      }
      const sections = [
        { key: 'equipe', title: 'Equipe', options: equipeOptions },
        { key: 'fin', title: 'Status financeiro', options: [
          { value: 'ALL', label: 'Todos' },
          { value: 'QUITADO', label: 'Quitados', count: c.quitados },
          { value: 'DEVENDO', label: 'Devendo', count: c.devendo },
          { value: 'NEGOCIADO', label: 'Negociados', count: c.negociados },
          { value: 'SEM_TOTAL', label: 'Sem total definido', count: c.semTotal }
        ]}
      ]
      sections.push(
        { key: 'ficha', title: 'Ficha de inscrição', options: [
          { value: 'ALL', label: 'Todos' },
          { value: 'COMPLETA', label: 'Ficha completa', count: c.fichaCompleta },
          { value: 'FALTA', label: 'Falta ficha', count: c.fichaFalta }
        ]},
        { key: 'tipo', title: 'Participação', options: [
          { value: 'ALL', label: 'Todos' },
          { value: 'COMPLETO', label: 'Completos', count: c.completos },
          { value: 'DIARIA', label: 'Diárias', count: c.diarias }
        ]},
        { key: 'acomodacao', title: 'Acomodação', options: [
          { value: 'ALL', label: 'Todas' },
          ...ACOMODACOES.map(opt => ({
            value: opt.value, label: opt.label, count: c.acomodacao[opt.value] || 0
          }))
        ]},
        { key: 'transporte', title: 'Transporte', options: [
          { value: 'ALL', label: 'Todos' },
          { value: 'PROPRIO', label: '🚗 Próprio', count: c.transporte.PROPRIO },
          { value: 'ONIBUS', label: '🚌 Ônibus', count: c.transporte.ONIBUS },
          { value: 'NONE', label: 'Não definido', count: c.transporte.NONE }
        ]}
      )
      return sections
    },
    activeChips() {
      const chips = []
      if (this.equipeFilter !== 'ALL') {
        const lbl = this.equipeFilter === 'NONE'
          ? 'Sem equipe'
          : this.equipes.find(e => e.value === this.equipeFilter)?.label || this.equipeFilter
        chips.push({ key: 'equipe', value: this.equipeFilter, label: lbl })
      }
      if (this.finFilter !== 'ALL') {
        const map = { QUITADO: 'Quitados', DEVENDO: 'Devendo', NEGOCIADO: '🤝 Negociados', SEM_TOTAL: 'Sem total' }
        if (map[this.finFilter]) chips.push({ key: 'fin', value: this.finFilter, label: map[this.finFilter] })
      }
      if (this.fichaFilter !== 'ALL') {
        chips.push({ key: 'ficha', value: this.fichaFilter, label: this.fichaFilter === 'COMPLETA' ? 'Ficha completa' : 'Falta ficha' })
      }
      if (this.tipoFilter !== 'ALL') {
        chips.push({ key: 'tipo', value: this.tipoFilter, label: this.tipoFilter === 'DIARIA' ? '📅 Diária' : 'Completo' })
      }
      if (this.transporteFilter !== 'ALL') {
        const map = { PROPRIO: '🚗 Próprio', ONIBUS: '🚌 Ônibus', NONE: 'Transporte não definido' }
        if (map[this.transporteFilter]) chips.push({ key: 'transporte', value: this.transporteFilter, label: map[this.transporteFilter] })
      }
      if (this.acomodacaoFilter !== 'ALL') {
        const opt = ACOMODACOES.find(a => a.value === this.acomodacaoFilter)
        chips.push({ key: 'acomodacao', value: this.acomodacaoFilter, label: opt?.label || this.acomodacaoFilter })
      }
      return chips
    },
    ACOMODACOES: () => ACOMODACOES,
    semEquipeCount() {
      return this.store.acampantes.filter(a => !a.equipe).length
    },
    counts() {
      let quitados = 0, devendo = 0, semTotal = 0, negociados = 0
      let fichaCompleta = 0, fichaFalta = 0
      let completos = 0, diarias = 0
      const acomodacao = { REDE: 0, BARRACA: 0, ALOJAMENTO: 0 }
      const transporte = { PROPRIO: 0, ONIBUS: 0, NONE: 0 }
      for (const a of this.store.acampantes) {
        const st = statusFinanceiro(a)
        if (st === 'quitado') quitados++
        else if (st === 'negociado') negociados++
        else if (st === 'devendo') devendo++
        else semTotal++
        if (a.fichaPreenchidaEm) fichaCompleta++
        else fichaFalta++
        if (isDiaria(a)) diarias++
        else completos++
        if (a.acomodacao && acomodacao[a.acomodacao] !== undefined) acomodacao[a.acomodacao]++
        if (a.transporte === 'PROPRIO') transporte.PROPRIO++
        else if (a.transporte === 'ONIBUS') transporte.ONIBUS++
        else transporte.NONE++
      }
      return { quitados, devendo, semTotal, negociados, fichaCompleta, fichaFalta, completos, diarias, acomodacao, transporte }
    },
    filtered() {
      let items = this.store.acampantes
      if (this.equipeFilter === 'NONE') items = items.filter(a => !a.equipe)
      else if (this.equipeFilter !== 'ALL') items = items.filter(a => a.equipe === this.equipeFilter)

      if (this.finFilter !== 'ALL') {
        const map = { QUITADO: 'quitado', DEVENDO: 'devendo', NEGOCIADO: 'negociado', SEM_TOTAL: 'sem-total' }
        const wanted = map[this.finFilter]
        if (wanted) items = items.filter(a => statusFinanceiro(a) === wanted)
      }

      if (this.fichaFilter === 'COMPLETA') {
        items = items.filter(a => !!a.fichaPreenchidaEm)
      } else if (this.fichaFilter === 'FALTA') {
        items = items.filter(a => !a.fichaPreenchidaEm)
      }

      if (this.tipoFilter === 'COMPLETO') {
        items = items.filter(a => !isDiaria(a))
      } else if (this.tipoFilter === 'DIARIA') {
        items = items.filter(a => isDiaria(a))
      }

      if (this.acomodacaoFilter !== 'ALL') {
        items = items.filter(a => a.acomodacao === this.acomodacaoFilter)
      }

      if (this.transporteFilter === 'NONE') {
        items = items.filter(a => !a.transporte)
      } else if (this.transporteFilter !== 'ALL') {
        items = items.filter(a => a.transporte === this.transporteFilter)
      }

      if (this.searchQuery.trim()) {
        const q = normalize(this.searchQuery)
        items = items.filter(a => normalize(a.name).includes(q) || normalize(a.contato).includes(q))
      }
      return items
    }
  },
  methods: {
    tribusLinkFor(item) { return buildTribusWaLink(item) },
    setFilter(key, value) {
      const next = { ...this.$route.query }
      if (value === null || value === undefined || value === '' || value === 'ALL') {
        delete next[key]
      } else {
        next[key] = value
      }
      this.$router.replace({ query: next }).catch(() => {})
    },
    clearAllFilters() {
      const keep = {}
      for (const k of Object.keys(this.$route.query || {})) {
        if (!['q', 'equipe', 'fin', 'ficha', 'tipo', 'acomodacao', 'transporte'].includes(k)) {
          keep[k] = this.$route.query[k]
        }
      }
      this.$router.replace({ query: keep }).catch(() => {})
    },
    onDrawerUpdate(next) {
      const query = { ...this.$route.query }
      const map = { equipe: 'equipe', fin: 'fin', ficha: 'ficha', tipo: 'tipo', acomodacao: 'acomodacao', transporte: 'transporte' }
      for (const [k, qk] of Object.entries(map)) {
        const v = next[k]
        if (!v || v === 'ALL') delete query[qk]
        else query[qk] = v
      }
      this.$router.replace({ query }).catch(() => {})
    },
    openNew() {
      this.formTarget = null
      this.formVisible = true
    },
    openEdit(item) {
      this.formTarget = item
      this.formVisible = true
    },
    openDetails(item) {
      this.detailsTarget = item
    },
    editFromDetails() {
      const target = this.detailsTarget
      this.detailsTarget = null
      if (target) this.openEdit(target)
    },
    qrFromDetails() {
      const target = this.detailsTarget
      this.detailsTarget = null
      if (target) this.openQr(target)
    },
    openFichaAdmin() {
      if (this.detailsTarget) this.fichaTarget = this.detailsTarget
    },
    handleFichaSaved({ acampante }) {
      if (acampante) {
        this.store.updateFromPusher(acampante)
        this.refreshDetailsTarget()
      }
      this.fichaTarget = null
      this.$toast.open({ message: 'Ficha salva', type: 'success', position: 'top-right' })
    },
    handleFichaReopened({ acampante }) {
      if (acampante) {
        this.store.updateFromPusher(acampante)
        this.refreshDetailsTarget()
      }
      this.fichaTarget = null
      this.$toast.open({
        message: 'Ficha reaberta — o link público volta a aceitar preenchimento',
        type: 'info',
        position: 'top-right',
        duration: 4000
      })
    },
    formatBRL,
    statusFinanceiro,
    isDiaria,
    diasShortChips,
    diasLongChips,
    refreshDetailsTarget() {
      if (!this.detailsTarget) return
      const fresh = this.store.acampantes.find(a => a.id === this.detailsTarget.id)
      if (fresh) this.detailsTarget = fresh
    },
    openPagamento() {
      this.pagamentoTarget = this.detailsTarget
    },
    async handleSavePagamento(payload) {
      if (!this.pagamentoTarget) return
      this.savingPagamento = true
      try {
        await this.store.addPagamento(this.pagamentoTarget.id, payload)
        this.$toast.open({ message: 'Pagamento registrado', type: 'success', position: 'top-right' })
        this.pagamentoTarget = null
        this.refreshDetailsTarget()
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao registrar pagamento'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.savingPagamento = false
      }
    },
    confirmDeletePagamento(pag) {
      if (!this.detailsTarget) return
      this.deletePagamentoTarget = { pag, participant: this.detailsTarget }
    },
    async handleDeletePagamento() {
      if (!this.deletePagamentoTarget) return
      this.deletingPagamento = true
      try {
        await this.store.removePagamento(this.deletePagamentoTarget.participant.id, this.deletePagamentoTarget.pag.id)
        this.$toast.open({ message: 'Pagamento removido', type: 'warning', position: 'top-right' })
        this.deletePagamentoTarget = null
        this.refreshDetailsTarget()
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao remover pagamento'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.deletingPagamento = false
      }
    },
    closeForm() {
      this.formVisible = false
      this.formTarget = null
    },
    async handleSave({ data }) {
      // Sucesso é notificado via Pusher (new-user / update-user) no App.vue
      // pra não duplicar toast quando a ação parte deste cliente.
      this.submitting = true
      try {
        if (this.formTarget) {
          await this.store.update(this.formTarget.id, data)
        } else {
          await this.store.create(data)
        }
        this.closeForm()
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao salvar'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.submitting = false
      }
    },
    openQr(item) {
      this.qrTarget = item
    },
    confirmDelete(item) {
      this.deleteTarget = item
    },
    async executeDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      try {
        await this.store.remove(this.deleteTarget.id)
        // Sucesso notificado via Pusher (remove-user) no App.vue.
        this.deleteTarget = null
      } catch (err) {
        const msg = err.response?.data?.message || 'Erro ao remover'
        this.$toast.open({ message: msg, type: 'error', position: 'top-right', duration: 4000 })
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-base);
  gap: var(--spacing-md);
}
.page-header .page-title { margin-bottom: 0; }

.title-count {
  display: inline-flex;
  align-items: center;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-muted);
  background: var(--color-bg-hover);
  padding: 2px 10px;
  border-radius: 999px;
  margin-left: 8px;
  vertical-align: middle;
}

.toolbar {
  display: flex;
  gap: var(--spacing-md);
  align-items: center;
  margin-bottom: var(--spacing-md);
}
.btn-filters {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: var(--color-bg-surface);
  color: var(--color-text-primary);
  border: 1.5px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}
.btn-filters:hover { border-color: var(--color-accent); color: var(--color-accent); }
.btn-filters.has-active { border-color: var(--color-accent); color: var(--color-accent); background: rgba(51, 102, 204, 0.05); }
.filters-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--color-accent);
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 800;
}

.active-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: var(--spacing-md);
  padding: 8px 0;
  border-top: 1px dashed var(--color-bg-separator);
  border-bottom: 1px dashed var(--color-bg-separator);
}
.active-chips-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-muted);
  margin-right: 4px;
}
.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 12px;
  background: rgba(51, 102, 204, 0.1);
  color: var(--color-accent);
  border: none;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition-fast);
}
.active-chip:hover { background: rgba(51, 102, 204, 0.18); }
.active-chip svg { opacity: 0.7; }
.active-chip:hover svg { opacity: 1; }
.active-clear {
  background: none;
  border: none;
  color: var(--color-danger);
  font-size: var(--text-xs);
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
  margin-left: auto;
}
.active-clear:hover { color: #c0392b; }

.chip-count {
  background: rgba(0, 0, 0, 0.12);
  color: inherit;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
}

.search-box { position: relative; flex: 1; max-width: 560px; }
.search-input { padding-left: 38px; padding-right: 38px; }
.search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: var(--color-text-muted); pointer-events: none;
}
.clear-btn {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--color-text-muted);
  padding: 4px; display: inline-flex;
}
.clear-btn:hover { color: var(--color-danger); }

.acampante-list { display: flex; flex-direction: column; gap: var(--spacing-md); }
.acampante-item {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--spacing-base); padding: var(--spacing-base);
  transition: var(--transition-fast);
  width: 100%;
  font-family: var(--font-primary);
  font-size: inherit;
  color: inherit;
  text-align: left;
  border: none;
  cursor: pointer;
}
.acampante-item:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-hover);
}
.acampante-item:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}
.item-main { display: flex; flex-direction: column; gap: var(--spacing-xs); min-width: 0; flex: 1; }
.item-name-row { display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap; }
.item-name { font-size: var(--text-md); font-weight: 600; color: var(--color-text-primary); }
.item-meta {
  display: flex; align-items: center; gap: var(--spacing-md); flex-wrap: wrap;
  font-size: var(--text-sm); color: var(--color-text-muted);
}
.item-status {
  display: inline-flex; align-items: center;
  font-size: var(--text-xs); font-weight: 700;
  padding: 2px 8px; border-radius: 10px;
}
.status-confirmed { background: rgba(46, 204, 113, 0.15); color: var(--color-success); }
.status-pending { background: rgba(243, 156, 18, 0.15); color: var(--color-warning); }

.fin-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 10px;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.3px;
}
.fin-chip-quitado { background: rgba(46, 204, 113, 0.15); color: var(--color-success); }
.fin-chip-devendo { background: rgba(231, 76, 60, 0.12); color: var(--color-danger); }
.fin-chip-negociado { background: rgba(245, 158, 11, 0.15); color: #b45309; }

.ficha-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 10px;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.3px;
}
.ficha-badge i { font-size: 10px; }
.ficha-badge-ok { background: #e6f4ea; color: #137333; }
.ficha-badge-falta { background: #f1f3f4; color: #5f6368; }

.diaria-badge {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px; border-radius: 10px;
  background: rgba(25, 118, 210, 0.12);
  color: #1976d2;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.4px;
}

.ficha-overlay { z-index: 200; }
.ficha-modal {
  max-width: 640px;
  width: 100%;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.ficha-modal-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.close-x {
  flex-shrink: 0;
  width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: none; cursor: pointer;
  color: var(--color-text-muted);
  border-radius: 50%;
}
.close-x:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }

.item-actions { display: flex; gap: var(--spacing-xs); flex-shrink: 0; }
.icon-btn {
  width: 36px; height: 36px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--color-bg-hover);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-fast);
}
.icon-btn:hover { background: var(--color-accent); color: #fff; border-color: var(--color-accent); }
.icon-btn.icon-danger:hover { background: var(--color-danger); border-color: var(--color-danger); }
.icon-btn.icon-tribus { background: #25d366; color: #fff; border-color: #25d366; }
.icon-btn.icon-tribus:hover { background: #1ebe5d; border-color: #1ebe5d; }

.qr-modal { text-align: center; max-width: 380px; }
.qr-name { font-size: var(--text-lg); font-weight: 600; margin: var(--spacing-sm) 0 var(--spacing-md); color: var(--color-text-primary); }
.qr-display { display: flex; justify-content: center; margin: var(--spacing-base) 0; }
.qr-hint { font-size: var(--text-xs); color: var(--color-text-muted); margin-top: var(--spacing-sm); }

.confirm-text { font-size: var(--text-sm); color: var(--color-text-secondary); margin-bottom: var(--spacing-base); }

@media (max-width: 600px) {
  .acampante-item { flex-direction: column; align-items: stretch; }
  .item-actions { justify-content: flex-end; }
}
</style>
